import Constants from 'expo-constants';

import {
  AddCreditAccountDocument,
  UserAccountsDocument,
} from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';

import type {
  CardData,
  EncryptionResult,
  IOrbitalInputVaulting,
  IVaultService,
  IWorldPayInputVaulting,
  OrbitalEncryptionResult,
  VaultResult,
  WorldpayEncryptionResult,
} from '..';
import { PaymentProcessor, usePaymentMethodStore } from '..';
import type { Logger } from '../core/Logger';

export interface VaultServiceConfig {
  storeId: string;
  cardData: CardData;
  logger: Logger;
}

/**
 * VaultService - Handles card vaulting via backend API
 *
 * This service calls the backend's addCreditAccount mutation to vault
 * encrypted card data. It supports both Orbital and Worldpay processors.
 *
 */
export class VaultService implements IVaultService {
  private logger: Logger;
  private config: VaultServiceConfig;

  constructor(config: VaultServiceConfig) {
    this.config = config;
    this.logger = config.logger;
  }

  async vaultCard(
    encryptionResult: EncryptionResult,
    processor: PaymentProcessor
  ): Promise<VaultResult> {
    this.logger.log('Vaulting card', { processor, encryptionResult });

    if (!this.config.cardData) {
      this.logger.logError('Card data is not found');
    }

    try {
      const input = this.buildMutationInput(encryptionResult, processor);

      this.logger.log('Calling addCreditAccount mutation', {
        processor,
        hasEncryptionResult: !!encryptionResult,
        input: JSON.stringify(input, null, 2),
      });

      const response = await this.addCreditAccountMutation({
        input,
      });

      const addedAccount = response?.data?.addCreditAccount;

      if (!addedAccount?.accountIdentifier && !addedAccount?.fdAccountId) {
        this.logger.logError('No account identifier returned from vault response');
      }

      this.logger.log('Vault successful', {
        processor,
        accountIdentifier: addedAccount.accountIdentifier,
      });
      usePaymentMethodStore.getState().setFailedVaulting(false);

      return {
        accountIdentifier: addedAccount.accountIdentifier,
        success: true,
      };
    } catch (error) {
      this.logger.logError('Vault failed', error);
      usePaymentMethodStore.getState().removeNewCardData();
      usePaymentMethodStore.getState().setFailedVaulting(true);

      throw error;
    } finally {
      // Remove all new card data
      usePaymentMethodStore.getState().removeNewCardData();
    }
  }

  private buildMutationInput(encryptionResult: EncryptionResult, processor: PaymentProcessor): any {
    if (!this.config.cardData) {
      this.logger.logError('Card data is not available');
    }

    const { postalCode, fullName: cardholderName, expiryMonth, expiryYear } = this.config.cardData;

    if (processor === PaymentProcessor.ORBITAL) {
      return this.buildOrbitalInputVaulting(
        encryptionResult as OrbitalEncryptionResult,
        cardholderName,
        expiryMonth,
        expiryYear,
        postalCode
      );
    } else {
      return this.buildWorldpayInputVaulting(
        encryptionResult as WorldpayEncryptionResult,
        cardholderName,
        expiryMonth,
        expiryYear,
        postalCode
      );
    }
  }

  private buildOrbitalInputVaulting(
    encryptionResult: OrbitalEncryptionResult,
    cardholderName: string,
    expiryMonth: string,
    expiryYear: string,
    postalCode: string
  ): IOrbitalInputVaulting {
    const nullFieldValue = null;
    const creditType = usePaymentMethodStore.getState().creditType;

    if (!creditType) {
      this.logger.logError('Credit type is not available');
      throw new Error('Credit type is required for vaulting');
    }

    return {
      billingAddress: {
        country: nullFieldValue,
        region: nullFieldValue,
        postalCode: postalCode,
        locality: nullFieldValue,
        streetAddress: nullFieldValue,
      },
      orbitalInput: {
        encryptedCardNum: encryptionResult.encryptedCardNum,
        encryptedCvv: encryptionResult.encryptedCvv,
        expiryMonth,
        expiryYear,
        pieFormat: encryptionResult.pieFormat,
        pieMode: encryptionResult.pieMode,
        piePhaseId: encryptionResult.piePhaseId.toString(),
        pieIntegrityCheck: encryptionResult.pieIntegrityCheck,
        pieSubscriberId: encryptionResult.pieSubscriberId,
        pieKeyID: encryptionResult.pieKeyID,
        cardBrand: creditType.toLowerCase(),
        bin: encryptionResult.bin,
      },
      fullName: cardholderName,
      storeId: this.config.storeId,
    };
  }

  private buildWorldpayInputVaulting(
    encryptionResult: WorldpayEncryptionResult,
    cardholderName: string,
    expiryMonth: string,
    expiryYear: string,
    postalCode: string
  ): IWorldPayInputVaulting {
    const nullFieldValue = null;
    const creditType = usePaymentMethodStore.getState().creditType;

    if (!creditType) {
      this.logger.logError('Credit type is not available');
      throw new Error('Credit type is required for vaulting');
    }

    return {
      billingAddress: {
        country: nullFieldValue,
        region: nullFieldValue,
        postalCode: postalCode,
        locality: nullFieldValue,
        streetAddress: nullFieldValue,
        unitNumber: nullFieldValue,
      },
      worldpayInput: {
        lowValueToken: encryptionResult.lowValueToken,
        cardBrand: creditType.toLowerCase(),
        expiryMonth: expiryMonth || encryptionResult.expiryMonth,
        expiryYear: expiryYear || encryptionResult.expiryYear,
        bin: encryptionResult.bin,
        last4: encryptionResult.last4,
      },
      fullName: cardholderName,
      storeId: this.config.storeId,
    };
  }

  private async addCreditAccountMutation(variables: { input: any }): Promise<any> {
    const feAlpha3CountryCode =
      Constants.expoConfig?.extra?.eas.buildRegion === 'US' ? 'USA' : 'CAN';

    return await legacyClient.mutate({
      mutation: AddCreditAccountDocument,
      variables,
      awaitRefetchQueries: true,
      refetchQueries: [
        { query: UserAccountsDocument, variables: { feCountryCode: feAlpha3CountryCode } },
      ],
    });
  }
}
